// src/lib/topic-videos.ts
// Curated free SAT prep video library organized by canonical topic.
//
// Every `topic` here MUST be one of the 30 canonical topic names from
// src/lib/sat/taxonomy.ts. Subskill-level video collections roll up
// under the canonical topic they belong to (e.g. "Pythagorean
// Theorem" videos live under "Right Triangles and Trigonometry", not
// as their own entry).
//
// The pin test in src/lib/__tests__/learn-canonical.test.ts enforces
// this — adding a non-canonical entry will fail CI.
//
// History: until 2026-05-10 this file used a mix of pre-#368 legacy
// topic names ("Linear Equations", "Right Triangles") and subskill
// names ("Punctuation", "Function Notation"). The canonical taxonomy
// migration (#368) renamed all Question.topic values to Title Case +
// merged umbrellas, leaving this file out of sync. This rewrite
// re-keys every entry under canonical topics and merges
// subskill-level videos into their parent.
//
// Link check, 2026-10-10: every id was resolved against YouTube's public oEmbed endpoint.
// The 16 that no longer exist (all labelled "Khan Academy SAT") were removed, durations
// were replaced with YouTube's own lengths (most had been estimates), and two sources were
// corrected to the channel that actually published the video. These lists also feed the
// /videos Reading & Writing collection (rw-video-collection.ts), so a dead link here is a
// dead card there too.

import { CANONICAL_TOPICS } from '@/lib/sat/taxonomy'
import { getCollection, publishedVideos } from './video-catalog'

export type TopicVideo = {
  title: string
  url: string
  duration: string // e.g. "12:34"
  source: string // e.g. "Khan Academy"
  /**
   * 9:16 rather than 16:9. Our own Shorts are vertical, and rendering them in the default
   * landscape frame letterboxes them into a thin sliver between two black bars.
   */
  vertical?: boolean
}

export type TopicVideoEntry = {
  topic: string
  section: 'RW' | 'MATH'
  videos: TopicVideo[]
}

/**
 * Extracts the YouTube video ID from a watch, short-link, Shorts or embed URL.
 * Returns null if the URL does not match any of them.
 *
 * `/shorts/` matters: our own Desmos series is published as YouTube Shorts, and a Shorts
 * link used to fall through this and return null — which silently broke BOTH the thumbnail
 * and the embed, with no error anywhere. Shorts are also served from /watch?v=, so a link
 * copied from the desktop player worked while one copied from the phone did not.
 */
export function getYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube(?:-nocookie)?\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  )
  return match ? match[1] : null
}

/**
 * Returns the YouTube thumbnail URL for a given video URL.
 */
export function getYouTubeThumbnail(url: string): string | null {
  const id = getYouTubeId(url)
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null
}

// ---------------------------------------------------------------------------
// Reading & Writing — 10 canonical topics
// ---------------------------------------------------------------------------

const rwTopics: TopicVideoEntry[] = [
  // ----- Information and Ideas -----
  {
    topic: 'Central Ideas and Details',
    section: 'RW',
    videos: [
      {
        title: 'What is a main idea? | Reading',
        url: 'https://www.youtube.com/watch?v=4swFGRhQoMI',
        duration: '5:14',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Command of Evidence',
    section: 'RW',
    videos: [
      {
        title: 'Command of Evidence on the Digital SAT: Strategies & Practice',
        url: 'https://www.youtube.com/watch?v=POcYofMngBw',
        duration: '18:43',
        source: 'Ivy League Mentors',
      },
      {
        title: 'Command of Textual Evidence — question walkthrough',
        url: 'https://www.youtube.com/watch?v=xhYvUV2NQ3g',
        duration: '46:31',
        source: '3-4-5 Tutoring',
      },
      {
        title: 'Command of Quantitative Evidence (Graphs) — Digital SAT',
        url: 'https://www.youtube.com/watch?v=J7Ka3-JwGxY',
        duration: '18:24',
        source: 'SAT Mastermind',
      },
    ],
  },
  {
    topic: 'Inferences',
    section: 'RW',
    videos: [
      {
        title: 'Inference Questions on the Digital SAT: Strategies & Practice',
        url: 'https://www.youtube.com/watch?v=ANkK_ecpZZY',
        duration: '14:22',
        source: 'Ivy League Mentors',
      },
      {
        title: 'Logically Completing the Text — question walkthrough',
        url: 'https://www.youtube.com/watch?v=TRFf-zAYuyE',
        duration: '31:42',
        source: '3-4-5 Tutoring',
      },
    ],
  },

  // ----- Craft and Structure -----
  {
    topic: 'Words in Context',
    section: 'RW',
    videos: [
      {
        title: 'Words in Context (Vocab) on the Digital SAT Reading & Writing',
        url: 'https://www.youtube.com/watch?v=jEYvlWifHN8',
        duration: '11:50',
        source: '3-4-5 Tutoring',
      },
      {
        title: 'Words in Context (Vocab): Strategies & Practice — Digital SAT',
        url: 'https://www.youtube.com/watch?v=dFQaoOGYhTk',
        duration: '8:15',
        source: 'Ivy League Mentors',
      },
    ],
  },
  {
    topic: 'Text Structure and Purpose',
    section: 'RW',
    videos: [
      {
        title: 'How to Solve Digital SAT Text Structure and Purpose Questions',
        url: 'https://www.youtube.com/watch?v=U3qWNZgoejU',
        duration: '9:46',
        source: 'TestPrepSHSAT',
      },
    ],
  },
  {
    topic: 'Cross-Text Connections',
    section: 'RW',
    videos: [
      {
        title: 'How to Approach Cross-Text Connections Questions (Digital SAT)',
        url: 'https://www.youtube.com/watch?v=EyqHcKfzBxo',
        duration: '10:22',
        source: 'Test Innovators',
      },
    ],
  },

  // ----- Expression of Ideas -----
  {
    topic: 'Rhetorical Synthesis',
    section: 'RW',
    videos: [
      {
        title: 'Rhetorical Synthesis on the Digital SAT: Strategies & Practice',
        url: 'https://www.youtube.com/watch?v=lo7LFbac_9o',
        duration: '7:29',
        source: 'Ivy League Mentors',
      },
      {
        title: 'Rhetorical Synthesis — question walkthrough',
        url: 'https://www.youtube.com/watch?v=sVtASxq4hng',
        duration: '18:47',
        source: '3-4-5 Tutoring',
      },
    ],
  },
  {
    topic: 'Transitions',
    section: 'RW',
    videos: [
      {
        title: 'Transitions on the Digital SAT — Full Guide',
        url: 'https://www.youtube.com/watch?v=eJ-PMwmareM',
        duration: '31:05',
        source: 'SAT Mastermind',
      },
    ],
  },

  // ----- Standard English Conventions -----
  {
    topic: 'Boundaries',
    section: 'RW',
    videos: [
      // (Previously a separate "Punctuation" entry — rolled in here
      //  since #368 merged Punctuation as a subskill of Boundaries.)
      {
        title: 'Introduction to the semicolon | Punctuation',
        url: 'https://www.youtube.com/watch?v=41XNKfR56OY',
        duration: '2:38',
        source: 'Khan Academy',
      },
      {
        title: 'Semicolons and complex lists | Punctuation',
        url: 'https://www.youtube.com/watch?v=xWJ3HoQTpgY',
        duration: '2:39',
        source: 'Khan Academy',
      },
      {
        title: 'Linking function of the colon | Punctuation',
        url: 'https://www.youtube.com/watch?v=0yZ0ehTLxoo',
        duration: '5:20',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Form, Structure, and Sense',
    section: 'RW',
    videos: [
      // Rolled in from the previously-separate "Subject-Verb Agreement",
      // "Verb Forms and Tenses", and "Pronoun Usage" entries — all
      // subskills of Form, Structure, and Sense per the canonical
      // taxonomy.
      {
        title: 'Subject-verb agreement | Syntax',
        url: 'https://www.youtube.com/watch?v=4fMipjAnlRk',
        duration: '4:53',
        source: 'Khan Academy',
      },
      {
        title: 'Introduction to verb tense | Grammar',
        url: 'https://www.youtube.com/watch?v=faUvT7zfsyk',
        duration: '1:46',
        source: 'Khan Academy',
      },
      {
        title: 'Pronoun-antecedent agreement | Syntax',
        url: 'https://www.youtube.com/watch?v=j9kIACViG60',
        duration: '4:06',
        source: 'Khan Academy',
      },
    ],
  },
]

// ---------------------------------------------------------------------------
// Math — 20 canonical topics
// ---------------------------------------------------------------------------

const mathTopics: TopicVideoEntry[] = [
  // ----- Algebra -----
  {
    topic: 'Linear Equations in 1 Variable',
    section: 'MATH',
    // Placeholder: its one curated video (a "Khan Academy SAT" link) no longer
    // resolves on YouTube and was dropped on 2026-10-10.
    videos: [],
  },
  {
    topic: 'Linear Equations in 2 Variables',
    section: 'MATH',
    videos: [
      // The slope-intercept video — about lines in the xy-plane,
      // which is exactly Linear Equations in 2 Variables territory.
      {
        title: 'Slope-intercept form | Algebra I',
        url: 'https://www.youtube.com/watch?v=IL3UCuXrUzE',
        duration: '8:59',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Linear Functions',
    section: 'MATH',
    videos: [
      {
        title: 'Introduction to slope | Algebra I',
        url: 'https://www.youtube.com/watch?v=MeU-KzdCBps',
        duration: '6:55',
        source: 'Khan Academy',
      },
      // Rolled in from the previously-separate "Function Notation"
      // entry — that's a subskill of Linear Functions (and also
      // Nonlinear Functions, but it's most foundational here).
      {
        title: 'Evaluating with function notation | Algebra II',
        url: 'https://www.youtube.com/watch?v=EmTvdKkAUtE',
        duration: '11:49',
        source: 'Khan Academy',
      },
      {
        title: 'Evaluating an expression with function notation (example) | Algebra I',
        url: 'https://www.youtube.com/watch?v=uaPm3Tpuxbc',
        duration: '1:23',
        source: 'Khan Academy',
      },
      {
        title: 'Interpreting an expression with function notation | Algebra I',
        url: 'https://www.youtube.com/watch?v=HERb3x0aw6c',
        duration: '3:23',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Systems of 2 Linear Equations in 2 Variables',
    section: 'MATH',
    videos: [
      {
        title: 'Solving systems of equations by elimination | Algebra Basics',
        url: 'https://www.youtube.com/watch?v=vA-55wZtLeE',
        duration: '12:43',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Linear Inequalities in 1 or 2 Variables',
    section: 'MATH',
    videos: [
      {
        title: 'Introduction to graphing inequalities | Algebra I',
        url: 'https://www.youtube.com/watch?v=unSBFwK881s',
        duration: '8:03',
        source: 'Khan Academy',
      },
      {
        title: 'LINEAR INEQUALITIES GRAPHING EXPLAINED!',
        url: 'https://www.youtube.com/watch?v=P_-c9D6mjGA',
        duration: '10:33',
        source: 'Mashup Math',
      },
    ],
  },

  // ----- Advanced Math -----
  {
    topic: 'Equivalent Expressions',
    section: 'MATH',
    videos: [
      // Rolled in from "Exponents and Radicals" — a subskill of
      // Equivalent Expressions per the canonical taxonomy.
      {
        title: 'Exponent rules part 1 | Pre-Algebra',
        url: 'https://www.youtube.com/watch?v=kITJ6qH7jS0',
        duration: '9:43',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
    section: 'MATH',
    videos: [
      // From the previous "Quadratic Equations" entry — quadratic
      // EQUATIONS belong here (functions go to Nonlinear Functions
      // below).
      {
        title: 'Solving a quadratic equation by factoring | Algebra II',
        url: 'https://www.youtube.com/watch?v=2ZzuZvz33X0',
        duration: '6:22',
        source: 'Khan Academy',
      },
      {
        title: 'How To Solve Quadratic Equations By Factoring',
        url: 'https://www.youtube.com/watch?v=qeByhTF8WEw',
        duration: '12:29',
        source: 'The Organic Chemistry Tutor',
      },
    ],
  },
  {
    topic: 'Nonlinear Functions',
    section: 'MATH',
    videos: [
      // Rolled in from the previously-separate "Exponential
      // Functions" entry — a subskill of Nonlinear Functions per the
      // canonical taxonomy.
      {
        title: 'Exponential growth functions | Algebra II',
        url: 'https://www.youtube.com/watch?v=6WMZ7J0wwMI',
        duration: '7:41',
        source: 'Khan Academy',
      },
      {
        title: 'Graphing exponential growth & decay | High School Math',
        url: 'https://www.youtube.com/watch?v=RVv0Jgi3Pbw',
        duration: '4:05',
        source: 'Khan Academy',
      },
      {
        title: 'Exponential growth and decay word problems | Algebra II',
        url: 'https://www.youtube.com/watch?v=m5Tf6vgoJtQ',
        duration: '7:21',
        source: 'Khan Academy',
      },
    ],
  },

  // ----- Problem-Solving and Data Analysis -----
  {
    topic: 'Ratios, Rates, Proportional Relationships, and Units',
    section: 'MATH',
    videos: [
      {
        title: 'Introduction to ratios | Pre-Algebra',
        url: 'https://www.youtube.com/watch?v=HpdMJaKaXXc',
        duration: '14:13',
        source: 'Khan Academy',
      },
      // Rolled in from the previously-separate "Unit Conversion"
      // entry — a subskill of Ratios/Rates per the canonical taxonomy.
      {
        title: 'Converting pounds to ounces | Ratios, proportions, units, and rates',
        url: 'https://www.youtube.com/watch?v=IrvEHVn-fks',
        duration: '1:34',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Percentages',
    section: 'MATH',
    videos: [
      {
        title: 'Growing by a percentage | Algebra I',
        url: 'https://www.youtube.com/watch?v=X2jVap1YgwI',
        duration: '9:43',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'One-Variable Data: Distributions and Measures of Center and Spread',
    section: 'MATH',
    videos: [
      // Renamed from the previous "Statistics" entry.
      {
        title: 'Finding mean, median, and mode | Probability and Statistics',
        url: 'https://www.youtube.com/watch?v=k3aKKasOmIw',
        duration: '3:55',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Two-Variable Data: Models and Scatterplots',
    section: 'MATH',
    videos: [
      // Renamed from the previous "Data Interpretation" entry —
      // scatterplots are the core of this canonical topic.
      {
        title: 'SAT Math: Scatterplots (Foundations)',
        url: 'https://www.youtube.com/watch?v=EBsGtgRs2YY',
        duration: '5:05',
        source: 'Scalar Learning',
      },
    ],
  },
  {
    topic: 'Probability and Conditional Probability',
    section: 'MATH',
    videos: [
      // Renamed from the previous "Probability" entry.
      {
        title: 'Two-way frequency tables and Venn diagrams | 8th grade',
        url: 'https://www.youtube.com/watch?v=l5MrtV7ZN88',
        duration: '6:22',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Inference from Sample Statistics and Margin of Error',
    section: 'MATH',
    videos: [
      {
        title: 'Confidence intervals and margin of error',
        url: 'https://www.youtube.com/watch?v=hlM7zdf7zwU',
        duration: '11:44',
        source: 'Khan Academy',
      },
      {
        title: 'Sample size for a target margin of error',
        url: 'https://www.youtube.com/watch?v=VyFs7fsWE6w',
        duration: '8:01',
        source: 'Khan Academy',
      },
      {
        title: 'Z-score, confidence interval & margin of error — worked example',
        url: 'https://www.youtube.com/watch?v=DT-fPG0Hff8',
        duration: '20:34',
        source: 'The Organic Chemistry Tutor',
      },
    ],
  },
  {
    topic: 'Evaluating Statistical Claims: Observational Studies and Experiments',
    section: 'MATH',
    videos: [
      {
        title: 'Types of statistical studies (sample, observational, experiment)',
        url: 'https://www.youtube.com/watch?v=SaP1O0i1bdc',
        duration: '10:31',
        source: 'Khan Academy',
      },
      {
        title: 'Introduction to experiment design',
        url: 'https://www.youtube.com/watch?v=DaBq0naj0YY',
        duration: '10:27',
        source: 'Khan Academy',
      },
    ],
  },

  // ----- Geometry and Trigonometry -----
  {
    topic: 'Area and Volume',
    section: 'MATH',
    videos: [
      // Rolled in from "Volume" and "Area and Perimeter" — both
      // covered by Area and Volume in the canonical taxonomy.
      {
        title: 'Cylinder volume and surface area | Geometry',
        url: 'https://www.youtube.com/watch?v=gL3HxBQyeg0',
        duration: '8:07',
        source: 'Khan Academy',
      },
      {
        title: 'Perimeter and area: the basics | Geometry',
        url: 'https://www.youtube.com/watch?v=LoaBd-sPzkU',
        duration: '8:25',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Lines, Angles, and Triangles',
    section: 'MATH',
    videos: [
      // Rolled in from the previously-separate "Angles and Lines" +
      // "Similar Triangles" entries.
      {
        title: 'Angles formed by parallel lines and transversals | Geometry',
        url: 'https://www.youtube.com/watch?v=H-E5rlpCVu4',
        duration: '7:07',
        source: 'Khan Academy',
      },
      {
        title: 'Angles formed between transversals and parallel lines | Geometry',
        url: 'https://www.youtube.com/watch?v=gRKZaojKeP0',
        duration: '7:53',
        source: 'Khan Academy',
      },
      {
        title: 'Similar triangles | Geometry',
        url: 'https://www.youtube.com/watch?v=BI-rtfZVXy0',
        duration: '9:34',
        source: 'Khan Academy',
      },
      {
        title: 'Similar triangle basics | Geometry',
        url: 'https://www.youtube.com/watch?v=9ThXDY9Y3oU',
        duration: '9:17',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Right Triangles and Trigonometry',
    section: 'MATH',
    videos: [
      // Rolled in from the previously-separate "Right Triangles",
      // "Special Right Triangles", and "Trigonometry" entries — all
      // covered by Right Triangles and Trigonometry in the canonical
      // taxonomy.
      {
        title: 'The Pythagorean theorem intro | Geometry',
        url: 'https://www.youtube.com/watch?v=AA6RfgP-AHU',
        duration: '10:46',
        source: 'Khan Academy',
      },
      {
        title: 'Pythagorean theorem | Geometry',
        url: 'https://www.youtube.com/watch?v=AEIzy1kNRqo',
        duration: '13:02',
        source: 'Khan Academy',
      },
      {
        title: '30-60-90 triangle side ratios proof | Geometry',
        url: 'https://www.youtube.com/watch?v=SFL4stapeUs',
        duration: '6:59',
        source: 'Khan Academy',
      },
      {
        title: 'Special Right Triangles - 45 45 90 - Trigonometry & Geometry | SAT Math',
        url: 'https://www.youtube.com/watch?v=xzMirNSv1d0',
        duration: '10:28',
        source: 'The Organic Chemistry Tutor',
      },
      {
        title: 'Basic trigonometry | Trigonometry',
        url: 'https://www.youtube.com/watch?v=Jsiy4TxgIME',
        duration: '9:17',
        source: 'Khan Academy',
      },
      {
        title: 'Introduction to the unit circle | Trigonometry',
        url: 'https://www.youtube.com/watch?v=1m9p9iubMLU',
        duration: '9:04',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Circles',
    section: 'MATH',
    videos: [
      // Rolled in from the previously-separate "Arc Length" entry —
      // a subskill of Circles per the canonical taxonomy.
      {
        title: 'Radian measure and arc length | Trigonometry',
        url: 'https://www.youtube.com/watch?v=axGgnXyuiTg',
        duration: '3:29',
        source: 'Khan Academy',
      },
      {
        title: 'Radians as ratio of arc length to radius | High school geometry',
        url: 'https://www.youtube.com/watch?v=9qrx0aqyhS8',
        duration: '6:40',
        source: 'Khan Academy',
      },
    ],
  },
  {
    topic: 'Coordinate Geometry',
    section: 'MATH',
    videos: [
      // UnlimitedTests-only canonical topic — drillable but every
      // question carries a parentTopic for College Board score
      // rollup. See src/lib/sat/taxonomy.ts.
      {
        title: 'Distance formula | Analytic geometry | Geometry',
        url: 'https://www.youtube.com/watch?v=nyZuite17Pc',
        duration: '9:39',
        source: 'Khan Academy',
      },
      {
        title: 'Midpoint formula | Analytic geometry | Geometry',
        url: 'https://www.youtube.com/watch?v=Ez_-RwV9WVo',
        duration: '6:41',
        source: 'Khan Academy',
      },
    ],
  },
]

/**
 * Complete topic video library (R&W + Math combined). Every entry's
 * `topic` is a canonical taxonomy name; verified by the pin test in
 * src/lib/__tests__/learn-canonical.test.ts.
 */
/**
 * Folds our own video library into the curated list, so each video surfaces inside the
 * lesson whose topic it teaches.
 *
 * Done here rather than by hand-writing entries above so the YouTube ids live in exactly
 * ONE place (video-catalog.ts). The /videos pages and the lessons therefore cannot drift
 * apart, and an unpublished video (empty id) simply does not appear rather than rendering a
 * dead embed. Ours lead their topic's list; VideoSection sorts on source as well.
 */
export const OUR_SOURCE = 'UnlimitedTests'

function withOurVideos(entries: TopicVideoEntry[]): TopicVideoEntry[] {
  const byTopic = new Map<string, TopicVideo[]>()
  for (const v of publishedVideos()) {
    const c = getCollection(v.collection)
    const list = byTopic.get(v.topic) ?? []
    list.push({
      title: c ? `${v.title} — ${c.title}, Ep. ${v.n}` : v.title,
      url: `https://www.youtube.com/watch?v=${v.youtubeId}`,
      duration: v.duration,
      source: OUR_SOURCE,
      vertical: true,
    })
    byTopic.set(v.topic, list)
  }
  return entries.map((e) =>
    byTopic.has(e.topic) ? { ...e, videos: [...byTopic.get(e.topic)!, ...e.videos] } : e,
  )
}

export const TOPIC_VIDEOS: TopicVideoEntry[] = withOurVideos([...rwTopics, ...mathTopics])

/**
 * Defensive runtime check — fails the import if anyone adds a
 * non-canonical topic. The unit test catches this earlier, but having
 * the check here too means even an ad-hoc seed script that imports
 * this file will surface the bug rather than silently injecting bad
 * topic strings into the bank.
 *
 * Disabled in production because we don't want a typo here to break
 * a Vercel deploy; the test gates it in CI.
 */
if (process.env.NODE_ENV !== 'production') {
  const canonical = new Set(CANONICAL_TOPICS)
  for (const entry of TOPIC_VIDEOS) {
    if (!canonical.has(entry.topic)) {
      throw new Error(
        `topic-videos: "${entry.topic}" is not a canonical taxonomy topic. ` +
          `See src/lib/sat/taxonomy.ts for the 30 canonical names.`,
      )
    }
  }
}

/**
 * Lookup videos by topic name (case-insensitive).
 */
export function getVideosForTopic(topic: string): TopicVideoEntry | undefined {
  return TOPIC_VIDEOS.find((t) => t.topic.toLowerCase() === topic.toLowerCase())
}

/**
 * Get all unique topic names.
 */
export function getAllTopicNames(): string[] {
  return TOPIC_VIDEOS.map((t) => t.topic)
}

/**
 * Get topics filtered by section.
 */
export function getTopicsBySection(section: 'RW' | 'MATH'): TopicVideoEntry[] {
  return TOPIC_VIDEOS.filter((t) => t.section === section)
}
